// focusTarget — the page and element arms of the openRef cluster's
// `FocusTarget`: build "open this source at page N" / "open this material at
// element E" and read them back at the render edge.
//
// Both are structural selectors over `Core.Anchor` path segments (the same
// shape `cite_source` builds), so a reader that resolves richer selectors
// (quote, heading) needs no new target shape.

export {
    elementFocusTarget,
    focusElementOf,
    focusPageOf,
    focusPagesOf,
    pageFocusTarget,
};

import * as Core from "@bilda/core";
import { List, Result } from "@bilda/utils";
import type { FocusTarget, PageSpan } from "./types.js";

// "Open at page `page`" (1-based), optionally marking the span through
// `lastPage`; `undefined` for a non-page number. A `lastPage` before `page`
// (or equal to it) is a single page.
const pageFocusTarget = (
    page: number,
    lastPage?: number,
): FocusTarget | undefined => {
    const r = Core.Anchor.toPageNumber(page);
    if (!Result.isOk(r)) return undefined;
    const selector = Core.Anchor.mkStructuralSelector(
        List.nonEmpty(Core.Anchor.mkPageSegment(r.value)),
    );
    return lastPage !== undefined && lastPage > page
        ? { selector, lastPage }
        : { selector };
};

// The page a target names, if any: the first page segment of a structural
// selector.
const focusPageOf = (target: FocusTarget | undefined): number | undefined => {
    const selector = target?.selector;
    if (selector?.type !== "structural_selector") return undefined;
    const { head, tail } = selector.path;
    const page = [head, ...tail].find((s) => s.type === "page_segment");
    return page?.type === "page_segment" ? page.page : undefined;
};

// The page span a target names, if any: its start page through `lastPage`
// (a single page when there is no later end).
const focusPagesOf = (
    target: FocusTarget | undefined,
): PageSpan | undefined => {
    const first = focusPageOf(target);
    if (first === undefined) return undefined;
    const last = target?.lastPage;
    return { first, last: last !== undefined && last > first ? last : first };
};

// "Open at element `id`" — a material node (question, passage, section).
const elementFocusTarget = (id: string): FocusTarget => ({
    selector: Core.Anchor.mkStructuralSelector(
        List.nonEmpty(
            Core.Anchor.mkElementSegment(Core.Resource.Element.mkElementId(id)),
        ),
    ),
});

// The element a target names, if any: the LAST element segment of a
// structural selector (the deepest named node).
const focusElementOf = (
    target: FocusTarget | undefined,
): string | undefined => {
    const selector = target?.selector;
    if (selector?.type !== "structural_selector") return undefined;
    const { head, tail } = selector.path;
    return [head, ...tail]
        .flatMap((s) => (s.type === "element_segment" ? [s.elementId] : []))
        .at(-1);
};
